// Design Tokens - Premium 2026 Anime Streaming Theme
// Dark mode only with cinematic liquid glass effects
// Inspired by: Material Design 3, Apple Liquid Glass, Netflix, Crunchyroll, Arc Browser, VisionOS

export const colors = {
  // Core - Pure black background as requested
  background: {
    // Pure black background
    '#050505': '#050505',
    // Elevated surfaces with glass effect
    glass: {
      // Base glass (for background elements)
      base: 'rgba(5, 5, 5, 0.6)',
      // Elevated surfaces (cards, panels)
      elevated: 'rgba(5, 5, 5, 0.45)',
      // Floating elements
      floating: 'rgba(5, 5, 5, 0.3)',
      // Highlight/glow areas
      highlight: 'rgba(5, 5, 5, 0.25)',
    },
    // Overlay for modals, etc.
    overlay: 'rgba(0, 0, 0, 0.7)',
    // Backdrop for blurred backgrounds
    backdrop: 'rgba(5, 5, 5, 0.8)',
  },

  // Brand Colors - Cinematic Palette
  brand: {
    // Primary - Electric Blue to Purple gradient
    primary: {
      // Main colors
      blue: '#00b4d8',
      cyan: '#00ffef',
      purple: '#8a2be2',
      pink: '#ff00ff',
      orange: '#ff6b35',

      // Gradient definitions
      gradients: {
        bluePurple: 'linear-gradient(135deg, #00b4d8 0%, #8a2be2 100%)',
        purpleCyan: 'linear-gradient(135deg, #8a2be2 0%, #00ffef 100%)',
        orangePink: 'linear-gradient(135deg, #ff6b35 0%, #ff00ff 100%)',
        cinematic: 'linear-gradient(45deg, #00b4d8, #8a2be2, #00ffef, #ff00ff)',
        aurora: 'linear-gradient(0deg, #00b4d8, #8a2be2, #00ffef, #ff00ff, #ff6b35)',

        // Glow effects
        glow: {
          blue: '0 0 20px rgba(0, 180, 216, 0.5)',
          cyan: '0 0 20px rgba(0, 255, 239, 0.5)',
          purple: '0 0 20px rgba(138, 43, 226, 0.5)',
          pink: '0 0 20px rgba(255, 0, 255, 0.5)',
        },
      },

      // Grayscale
      grayscale: {
        '0': '#050505',
        '10': '#111111',
        '20': '#212121',
        '30': '#323232',
        '40': '#424242',
        '50': '#525252',
        '60': '#626262',
        '70': '#727272',
        '80': '#828282',
        '90': '#929292',
        '100': '#ffffff',
      },

      // Shadows
      shadows: {
        // Default shadows
        xs: '0 0px 1px 0px rgba(0, 0, 0, 0.05)',
        sm: '0 1px 2px 0px rgba(0, 0, 0, 0.05)',
        md: '0 1px 3px 0px rgba(0, 0, 0, 0.06)',
        lg: '0 2px 4px 0px rgba(0, 0, 0, 0.07)',
        xl: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
        '2xl': '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        '3xl': '0 10px 20px -5px rgba(0, 0, 0, 0.1)',
        '4xl': '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        '5xl': '0 25px 30px -5px rgba(0, 0, 0, 0.1)',
        '6xl': '0 30px 40px -5px rgba(0, 0, 0, 0.1)',
        // Button hover
        'hover-button': '0 4px 6px -1px rgba(0, 218, 216, 0.15)',
        'focus-button': '0 4px 6px -1px rgba(0, 218, 216, 0.1)',
      },
    },
  },
};

// Typography
export const typography = {
  fontFamily:
    '"Inter var", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: {
    fontSize: '3.5rem',
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.5px',
    background: 'linear-gradient(90deg, #00b4d8, #8a2be2, #00ffef)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
  },
  h2: {
    fontSize: '2.75rem',
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.5px',
    background: 'linear-gradient(90deg, #8a2be2, #00ffef)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
  },
  h3: {
    fontSize: '2.25rem',
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.5px',
    color: '#ffffff',
  },
  h4: {
    fontSize: '1.75rem',
    fontWeight: 600,
    lineHeight: 1.5,
    letterSpacing: '-0.5px',
    color: '#ffffff',
  },
  h5: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '-0.5px',
    color: '#ffffff',
  },
  h6: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.5px',
    color: '#ffffff',
  },
  subtitle1: {
    fontSize: '1.125rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '-0.5px',
    color: '#ffffff',
  },
  subtitle2: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '-0.5px',
    color: '#ffffff',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '-0.5px',
    color: '#ffffff',
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '-0.5px',
    color: '#ffffff',
  },
};

// Spacing
export const spacing = {
  '0': '0px',
  '1': '2px',
  '2': '4px',
  '3': '6px',
  '4': '8px',
  '5': '10px',
  '6': '12px',
  '7': '14px',
  '8': '16px',
  '9': '18px',
  '10': '20px',
  '11': '22px',
  '12': '24px',
  '13': '26px',
  '14': '28px',
  '15': '30px',
  '16': '32px',
};

// Border Radius
export const borderRadius = {
  'none': '0px',
  'xs': '2px',
  'sm': '4px',
  'md': '6px',
  'lg': '8px',
  'xl': '10px',
  '2xl': '12px',
  '3xl': '16px',
  '4xl': '20px',
  '5xl': '24px',
};

// Shadows
export const shadows = {
  // Default shadows
  xs: '0 0px 1px 0px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 2px 0px rgba(0, 0, 0, 0.05)',
  md: '0 1px 3px 0px rgba(0, 0, 0, 0.06)',
  lg: '0 2px 4px 0px rgba(0, 0, 0, 0.07)',
  xl: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
  '2xl': '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  '3xl': '0 10px 20px -5px rgba(0, 0, 0, 0.1)',
  '4xl': '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  '5xl': '0 25px 30px -5px rgba(0, 0, 0, 0.1)',
  '6xl': '0 30px 40px -5px rgba(0, 0, 0, 0.1)',
  // Button hover
  'hover-button': '0 4px 6px -1px rgba(0, 218, 216, 0.15)',
  'focus-button': '0 4px 6px -1px rxgba(0, 218, 216, 0.1)',
};

// Transitions
export const transitions = {
  fast: '150ms ease-in',
  normal: '250ms ease-in-out',
  slow: '350ms ease-in-out',
};

// Animation duration
export const animationDuration = {
  fast: '100ms',
  normal: '200ms',
  slow: '300ms',
};

// Z-index
export const zIndex = {
  // Layout
  header: 1000,
  // Overlays
  modal: 2000,
  popup: 3000,
  // Feedback
  toast: 4000,
  // Overlay
  overlay: 5000,
};

// Opacity
export const opacity = {
  '0': '0',
  '10': '0.1',
  '20': '0.2',
  '30': '0.3',
  '40': '0.4',
  '50': '0.5',
  '60': '0.6',
  '70': '0.7',
  '80': '0.8',
  '90': '0.9',
  '100': '1',
};

// Blur
export const blur = {
  '0': '0px',
  '2': '2px',
  '4': '4px',
  '6': '6px',
  '8': '8px',
  '10': '10px',
  '20': '20px',
  '30': '30px',
  '40': '40px',
};

// Grid
export const gridContainer = {
  // 1300px - Page
  '1300px': 'rem(1300px)',
  // 1100px - Page
  '1100px': 'rem(1100px)',
  // 1000px - Page
  '1000px': 'rem(1000px)',
  // 800px - Page
  '800px': 'rem(800px)',
  // 600px - Page
  '600px': 'rem(600px)',
  // 400px - Page
  '400px': 'rem(400px)',
  // 200px - Page
  '200px': 'rem(200px)',
};

// Breakpoints
export const breakpoints = {
  xs: '0px',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  '2xl': '1400px',
};

// Backwards compatibility exports for theme.ts
const getDesignTokens = () => ({
  breakpoints,
  spacing: Object.fromEntries(Object.entries(spacing).map(([k, v]) => [k, parseInt(v) || 0])),
  borderRadius: { ...borderRadius, default: borderRadius.md, full: '9999px' },
  typography: {
    fontFamily: { base: typography.fontFamily, heading: typography.fontFamily },
    fontSize: {
      '5xl': '3.5rem',
      '4xl': '2.75rem',
      '3xl': '2.25rem',
    },
    fontWeight: { extrabold: 800, bold: 700, semibold: 600 },
  },
  colors: {
    brand: {
      primary: { main: '#8B5CF6', light: '#A78BFA', dark: '#7C3AED' },
      secondary: { main: '#00B4D8' },
      accent: { main: '#EC4899' },
    },
    border: { default: 'rgba(255,255,255,0.08)' },
    dark: {
      surface: { main: 'rgba(5,5,5,0.6)', elevated: 'rgba(5,5,5,0.45)' },
    },
  },
  shadows: {
    sm: '0 1px 2px 0px rgba(0, 0, 0, 0.05)',
    md: '0 1px 3px 0px rgba(0, 0, 0, 0.06)',
    lg: '0 2px 4px 0px rgba(0, 0, 0, 0.07)',
    xl: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    '2xl': '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
  },
  glassmorphism: {
    background: 'rgba(5, 5, 5, 0.6)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.08)',
  },
});

export const designTokens = getDesignTokens();

export const dark = {
  background: { default: '#050505', paper: '#0A0A0F' },
  text: { primary: '#F8FAFC', secondary: '#94A3B8' },
  divider: 'rgba(255,255,255,0.08)',
};

export const light = {
  background: { default: '#F8FAFC', paper: '#FFFFFF' },
  text: { primary: '#0F172A', secondary: '#64748B' },
  divider: 'rgba(0,0,0,0.08)',
};